import React, { PureComponent, ReactNode } from 'react';
import { Flex } from '../../common/Flex';
import { TvDetails } from '../tv/details/TvDetails';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Button, Checkbox, Col, Divider, Input, notification, Row, Select, Space, Spin } from 'antd';
import {
    CapacityModel,
    CapacityValue,
    CountType,
    MCDM_ONLY_DTO_CLAZZ_VALUE,
    McdmApprovalState,
    McdmState,
    MeasureCancelRequestDto,
    MeasureCreateRequestDto,
    MeasureSubType,
    MeasureType,
    MeasureUpdateRequestDto,
    Otmv,
    RegInitialConstraint,
    REGULATION_DTO_CLAZZ_VALUE,
    REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
    REGULATION_PROPOSAL_DTO_CLAZZ_VALUE,
    RegulationDto,
    RegulationListRequestDto,
    RegulationOrMcdmOnlyDto,
    RegulationProposalAction,
    RegulationProposalDto,
    RegulationProposalFilingRequestDto,
    RegulationProposalKind,
    RegulationProposalRevocationRequestDto,
    RegulationProposalUpdateRequestDto,
    RegulationReasonType,
    RegulationState,
    TimePeriod,
    TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
} from 'app/model/CORE';
import TimeUtils from '../../../common/util/TimeUtils';
import { ModalPanelProps } from '../DashboardModal';
import MeasureUtils from '../../../common/util/MeasureUtils';
import InputTime from '../../../common/input/InputTime';
import TextArea from 'antd/es/input/TextArea';
import FlightLevelUtils from '../../../common/util/FlightLevelUtils';
import { InputDuration } from 'app/common/input/InputDuration';
import Utils from '../../../common/util/Utils';
import { LoadingOutlined, RedoOutlined } from '@ant-design/icons';
import { EnhancedIcon } from 'app/common/input/EnhancedIcon';
import './RegulationEditorPanel.less';
import { SlotListPanel } from './SlotListPanel';
import { SidePanelSeparator } from '../../common/SidePanelSeparator';
import { SlotListSynthesisForMeasure } from './SlotListSynthesis';
import TimePeriodUtils from '../../../common/util/TimePeriodUtils';
import ConditionalRender from '../../../common/components/ConditionalRender';
import AppContext from '../../../common/context/AppContext';
import { InitialConstraintsEditor } from './InitialConstraintsEditor';
import { Solution, SolutionType } from 'app/model/SOL49';
import { SolutionImpactPanel } from '../solver/SolutionImpactPanel';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { EntityType } from 'app/model/client/SOL49';
import { PartialDashboardDataUpdateEvent } from 'app/common/eventbus/Events';
import { OccupancyConstraintsEditor } from 'app/components/panels/measure/OccupancyConstraintsEditor';

export enum DisplayMeasureType {
    NETWORK_MEASURE = 'NETWORK_MEASURE',
    FMP_MEASURE = 'FMP_MEASURE',
    NETWORK_CHERRY_PICK_MEASURE = 'NETWORK_CHERRY_PICK_MEASURE',
    FMP_CHERRY_PICK_MEASURE = 'FMP_CHERRY_PICK_MEASURE',
}

export enum MCDMKind {
    NO = 'NO',
    YES = 'YES',
    MCDM_ONLY = 'MCDM_ONLY',
}

const EMPTY_REG: RegulationDto = {
    clazz: REGULATION_DTO_CLAZZ_VALUE,
    regulationState: null,
    subType: MeasureSubType.GROUND_DELAY,
    initialConstraints: [{}],
    cherryPicking: false,
    mcdmRequired: false,
    autoLink: false,
    updateCapacityRequired: true,
    updateTvActivationRequired: true,
    location: {
        clazz: TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
        description: '',
    },
    noDelayWindowInMs: 10 * TimeUtils.MIN_TO_MS,
    remark: '',
    calculationType: CountType.ENTRY,
    occupancyDurationInMs: TimeUtils.MIN_TO_MS,
    occupancyConstraints: [{}],
};

const EMPTY_REG_PROPOSAL: RegulationProposalDto = Object.assign({}, EMPTY_REG, {
    clazz: REGULATION_PROPOSAL_DTO_CLAZZ_VALUE,
    action: RegulationProposalAction.CREATION,
    kind: RegulationProposalKind.RegulationProposalWithoutProposalFlights,
});

interface RegulationEditorProps extends ModalPanelProps {
    currentTime: Date;
    id: string;
    idType: 'tv' | 'reg';
    initialRegulation?: Partial<RegulationDto>;
    readonly?: boolean;
}

interface RegulationEditorState {
    regulation: RegulationOrMcdmOnlyDto;
    countType: CountType;
    timePeriod: TimePeriod;
    sidePanelVisible: boolean;
    sidePanelContent: ReactNode;
    sidePanelWidth: string;
    loadingAutoPeriod: boolean;
    windowWidthBlinking: boolean;
    loading: boolean;
    outdated?: boolean;
    lastUpdate: Date;
}

function getDisplayMeasureType(regulation: RegulationOrMcdmOnlyDto): DisplayMeasureType {
    if (regulation?.createdByFmp) {
        if (regulation?.cherryPicking) {
            return DisplayMeasureType.FMP_CHERRY_PICK_MEASURE;
        } else {
            return DisplayMeasureType.FMP_MEASURE;
        }
    } else if (regulation?.cherryPicking) {
        return DisplayMeasureType.NETWORK_CHERRY_PICK_MEASURE;
    } else {
        return DisplayMeasureType.NETWORK_MEASURE;
    }
}

function getMcdmKind(regulation: RegulationOrMcdmOnlyDto) {
    if (regulation && MeasureUtils.isMcdmOnly(regulation)) {
        return MCDMKind.MCDM_ONLY;
    } else if (regulation?.mcdmRequired) {
        return MCDMKind.YES;
    }
    return MCDMKind.NO;
}

const getInitialState = isProposal => {
    return {
        regulation: Object.assign({}, isProposal ? EMPTY_REG_PROPOSAL : EMPTY_REG),
        countType: CountType.ENTRY,
        timePeriod: null,
        sidePanelVisible: false,
        sidePanelContent: null,
        sidePanelWidth: '0em',
        loadingAutoPeriod: false,
        windowWidthBlinking: false,
        loading: false,
        outdated: false,
        lastUpdate: AppContext.getCurrentTime(),
    };
};

export class RegulationEditorPanel extends PureComponent<RegulationEditorProps, RegulationEditorState> {
    private wwBlinkingEndDate: Date;
    public static INITIAL_WIDTH = '70em';
    state = Object.assign({}, getInitialState(AuthorisationUtils.isFmpUser()));

    constructor(props) {
        super(props);
        this.state.timePeriod = AppContext.getMonitoringPeriod();
    }

    componentDidMount(): void {
        this.refresh();
    }
    componentDidUpdate(prevProps) {
        if (this.props?.idType !== prevProps?.idType || this.props?.id !== prevProps?.id) {
            this.setState(Object.assign({}, getInitialState(AuthorisationUtils.isFmpUser())), () => this.refresh());
        }
    }
    isProposal = () => {
        return this.state.regulation?.clazz === REGULATION_PROPOSAL_DTO_CLAZZ_VALUE;
    };
    refresh = () => {
        if (this.props?.id && this.props.idType === 'reg') {
            const req: RegulationListRequestDto = {
                clazz: REGULATION_LIST_REQUEST_DTO_CLAZZ_VALUE,
                dataset: AppContext.getDataset(),
                timePeriod: AppContext.getTodayPeriod(),
                fields: MeasureUtils.ALL_REGULATION_FIELDS_NON_RD,
                regulIds: [this.props.id],
            };
            ServiceFactory.coreService()
                .getRegulations(req)
                .then(reply => {
                    if (reply?.data?.measures?.length === 1) {
                        // Extend display period according to regulation period
                        const timePeriod = TimePeriodUtils.copy(this.state.timePeriod);
                        let extendedApplicability = reply?.data?.measures[0]?.applicability;
                        if (extendedApplicability) {
                            extendedApplicability = {
                                from: extendedApplicability.from,
                                to: TimeUtils.addMinutes(extendedApplicability.to, 60), // End of regulation + 60mn
                            };
                        }
                        TimePeriodUtils.growToCover(timePeriod, extendedApplicability);
                        const regulation = reply?.data?.measures[0] as RegulationOrMcdmOnlyDto;

                        this.setState({
                            regulation: regulation,
                            countType: MeasureUtils.isEntryRegulation(regulation)
                                ? CountType.ENTRY
                                : CountType.OCCUPANCY,
                            timePeriod: timePeriod,
                        });
                    } else {
                        notification.error({
                            message: 'Error',
                            description: 'Expected 1 measure, but retrieve none or multiple ones',
                            duration: 0,
                        });
                    }
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        } else if (this.props?.id && this.props.idType === 'tv') {
            // let regulation = Object.assign({}, EMPTY_REG, {location: {id: this.props?.id, description: "SHOULD NOT BE EMPTY"}})
            // this.setState({regulation: regulation})
            if (this.props.initialRegulation) {
                this.setState(
                    {
                        countType: MeasureUtils.isEntryRegulation(this.props.initialRegulation)
                            ? CountType.ENTRY
                            : CountType.OCCUPANCY,
                    },
                    () => this.regulationStateUpdate(this.props.initialRegulation),
                );
            } else {
                this.updateTvId(this.props?.id);
            }
        }
    };
    updateRegulation = () => {
        this.setState({ loading: true });
        const measure = Object.assign({}, this.state.regulation, { scenarioReference: null }); // TODO: Remove after the exercise (no scenario on the replay platform)
        const req: MeasureUpdateRequestDto = {
            computeNetworkImpactAssessment: false,
            dataset: AppContext.getDataset(),
            measure: this.adaptRegulationForB2BRequest(measure),
            waitUntilApplied: true,
        };
        ServiceFactory.stamCoreService()
            .updateMeasure(req)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false }, () => this.handleSlotListClick());
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    createRegulation = () => {
        this.setState({ loading: true });
        const req: MeasureCreateRequestDto = {
            computeNetworkImpactAssessment: false,
            dataset: AppContext.getDataset(),
            measure: this.adaptRegulationForB2BRequest(this.state.regulation),
            waitUntilApplied: true,
        };
        ServiceFactory.stamCoreService()
            .createMeasure(req)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false }, () => this.handleSlotListClick());
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    cancelRegulation = () => {
        this.setState({ loading: true });
        const req: MeasureCancelRequestDto = {
            computeNetworkImpactAssessment: false,
            dataset: AppContext.getDataset(),
            day: AppContext.getMonitoredDay(),
            dataId: this.state.regulation?.dataId,
            measureId: this.state.regulation?.measureId,
        };
        ServiceFactory.stamCoreService()
            .cancelMeasure(req)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    createProposal = (action: RegulationProposalAction) => {
        this.setState({ loading: true });
        const regulationProposal: RegulationProposalDto = MeasureUtils.createProposalFromRegulation(
            this.state.regulation,
            action,
        );
        const req: RegulationProposalFilingRequestDto = {
            dataset: AppContext.getDataset(),
            regulation: this.adaptRegulationForB2BRequest(regulationProposal),
            normalRegulationDataId: action === RegulationProposalAction.CREATION ? null : this.state.regulation.dataId,
        };
        ServiceFactory.stamCoreService()
            .createRegulationProposal(req)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false }, () => {
                        this.props.onClose();
                        PartialDashboardDataUpdateEvent.fireEvent(EntityType.MEASURE_PROPOSAL);
                    });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    updateProposal = () => {
        this.setState({ loading: true });

        let promise;
        if (this.state.regulation.clazz === REGULATION_PROPOSAL_DTO_CLAZZ_VALUE) {
            const regulationProposal: RegulationProposalDto = Object.assign({}, this.state.regulation);

            const req: RegulationProposalUpdateRequestDto = {
                dataset: AppContext.getDataset(),
                regulation: this.adaptRegulationForB2BRequest(regulationProposal),
            };
            promise = ServiceFactory.stamCoreService().updateRegulationProposal(req);
        } else {
            const regulationProposal: RegulationProposalDto = Object.assign({}, this.state.regulation, {
                clazz: REGULATION_PROPOSAL_DTO_CLAZZ_VALUE,
                action: RegulationProposalAction.CREATION,
                //approvalState: McdmApprovalState.
                kind: RegulationProposalKind.RegulationProposalWithoutProposalFlights,
                mcdmState: McdmState.PROPOSED,
                remark: this.state.regulation.remark || '',
                dataId: null,
                //proposalFeedback? : string;
                //proposalNote? : string;
                //regulationState: RegulationState.
            });
            const req: RegulationProposalFilingRequestDto = {
                dataset: AppContext.getDataset(),
                regulation: this.adaptRegulationForB2BRequest(regulationProposal),
            };

            promise = ServiceFactory.stamCoreService().createRegulationProposal(req);
        }

        promise
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };
    cancelProposal = () => {
        this.setState({ loading: true });
        const req: RegulationProposalRevocationRequestDto = {
            dataset: AppContext.getDataset(),
            regulId: this.state.regulation.measureId.id,
        };
        ServiceFactory.stamCoreService()
            .cancelRegulationProposal(req)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else {
                    this.setState({ regulation: reply?.data, outdated: false });
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => this.setState({ loading: false }));
    };

    adaptRegulationForB2BRequest = (measure: RegulationOrMcdmOnlyDto): RegulationOrMcdmOnlyDto => {
        const newMeasure = { ...measure };
        if (measure.calculationType === CountType.ENTRY) {
            newMeasure.occupancyConstraints = null;
            newMeasure.occupancyDurationInMs = MeasureUtils.NO_DURATION_VALUE;
        } else {
            newMeasure.initialConstraints = null;
            newMeasure.supplementaryConstraints = null;
            newMeasure.noDelayWindowInMs = MeasureUtils.NO_DURATION_VALUE;
        }
        return newMeasure;
    };

    getDefaultWindowWidth = (initialConstraints: Array<RegInitialConstraint>) => {
        const highestRate = Math.max(
            ...initialConstraints.map(regInitialConstraint => MeasureUtils.getRate(regInitialConstraint)),
        );
        if (highestRate > 30) {
            return 6 * TimeUtils.MIN_TO_MS;
        } else if (highestRate > 12) {
            return 10 * TimeUtils.MIN_TO_MS;
        } else if (highestRate > 6) {
            return Math.floor((60 / highestRate) * 2) * TimeUtils.MIN_TO_MS;
        } else if (highestRate > 0) {
            return 20 * TimeUtils.MIN_TO_MS;
        } else {
            return 10 * TimeUtils.MIN_TO_MS;
        }
    };
    unblinkWindowWidthField = () => {
        if (TimeUtils.NOW().getTime() >= this.wwBlinkingEndDate.getTime()) {
            this.setState({ windowWidthBlinking: false });
        } else {
            setTimeout(this.unblinkWindowWidthField, 1000);
        }
    };
    blinkWindowWidthField = () => {
        // WW input blinks for 1s
        // Deals with multiple consecutive updates (multiple pending callbacks setting value to false)
        this.wwBlinkingEndDate = TimeUtils.addMillis(TimeUtils.NOW(), 1000);
        this.setState({ windowWidthBlinking: true }, this.unblinkWindowWidthField);
    };
    regulationStateUpdate = <K extends keyof RegulationEditorState>(
        update: Partial<RegulationOrMcdmOnlyDto>,
        stateUpdate?: Partial<RegulationEditorState>,
    ) => {
        const isInitialConstraintUpdate = Utils.isNotEmpty(update.initialConstraints);
        const isOccupancyConstraintUpdate = Utils.isNotEmpty(update.occupancyConstraints);
        if (isInitialConstraintUpdate) {
            update.noDelayWindowInMs = this.getDefaultWindowWidth(update.initialConstraints);
            this.blinkWindowWidthField();
        }
        const updatedRegulation = Object.assign({}, this.state.regulation, update);
        const updatedState: Pick<RegulationEditorState, K> = Object.assign(
            {},
            {
                regulation: MeasureUtils.adaptConstraintPeriods(
                    updatedRegulation,
                    isInitialConstraintUpdate,
                    isOccupancyConstraintUpdate,
                ),
                outdated: true,
            },
            stateUpdate as Pick<RegulationEditorState, K>,
        );
        this.setState(updatedState);
    };
    updateIdentifier = (id: string) => {
        this.regulationStateUpdate({
            measureId: {
                id: id.toUpperCase(),
                type: this.state.regulation?.measureId?.type || MeasureType.REGULATION,
            },
        });
    };
    updateTvId = (value: string) => {
        this.regulationStateUpdate({
            location: {
                clazz: this.state.regulation?.location?.clazz || TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
                id: value,
                description: this.state.regulation?.location?.description,
                flightLevels: this.state.regulation?.location?.flightLevels,
                refLocation: this.state.regulation?.location?.refLocation,
                tvSetIds: this.state.regulation?.location?.tvSetIds,
            },
            measureId: {
                id: MeasureUtils.getRegulationId(value),
                type: this.state.regulation?.measureId?.type || MeasureType.REGULATION,
            },
        });
    };

    updateMeasureType = (displayMeasureType: DisplayMeasureType) => {
        switch (displayMeasureType) {
            case DisplayMeasureType.FMP_CHERRY_PICK_MEASURE:
                return this.regulationStateUpdate({ createdByFmp: true, cherryPicking: true });
            case DisplayMeasureType.FMP_MEASURE:
                return this.regulationStateUpdate({ createdByFmp: true, cherryPicking: false });
            case DisplayMeasureType.NETWORK_MEASURE:
                return this.regulationStateUpdate({ createdByFmp: false, cherryPicking: false });
            case DisplayMeasureType.NETWORK_CHERRY_PICK_MEASURE:
                return this.regulationStateUpdate({ createdByFmp: false, cherryPicking: true });
        }
    };

    updateStartTime = (startTime: Date) => {
        let from = null;

        if (startTime) {
            // Start time date has to be TODAY
            from = TimeUtils.mergeDateAndTime(AppContext.getMonitoredDay(), startTime);
        }
        return this.regulationStateUpdate({
            applicability: {
                from: from,
                to: this.state.regulation?.applicability?.to,
            },
        });
    };

    updateEndTime = (endTime: Date) => {
        let to = null;
        if (endTime) {
            // End time date will either be the day of the start time, or the next day, or TODAY()
            let day =
                (this.state.regulation?.applicability?.from &&
                    TimeUtils.withoutTimeOfDay(this.state.regulation?.applicability?.from)) ||
                AppContext.getMonitoredDay();
            if (
                this.state.regulation?.applicability?.from &&
                TimeUtils.timeSinceMidnightInMs(endTime) <=
                    TimeUtils.timeSinceMidnightInMs(this.state.regulation?.applicability?.from)
            ) {
                day = TimeUtils.addDays(day, 1);
            }
            to = TimeUtils.addMillis(day, TimeUtils.timeSinceMidnightInMs(endTime));
        }
        return this.regulationStateUpdate({
            applicability: {
                from: this.state.regulation?.applicability?.from,
                to: to,
            },
        });
    };

    getDuration = (): number => {
        if (this.state.regulation?.applicability?.from && this.state.regulation?.applicability?.to) {
            return TimeUtils.diffInMs(
                this.state.regulation?.applicability?.to,
                this.state.regulation?.applicability?.from,
            );
        }
    };

    updateDuration = (duration: number) => {
        if (this.state.regulation?.applicability?.from) {
            this.updateEndTime(TimeUtils.addMillis(this.state.regulation?.applicability?.from, duration));
        } else if (this.state.regulation?.applicability?.to) {
            this.updateStartTime(TimeUtils.addMillis(this.state.regulation?.applicability?.to, -1 * duration));
        }
    };

    updateMcdmKind = (mcdmKind: MCDMKind) => {
        switch (mcdmKind) {
            case MCDMKind.NO:
                return this.regulationStateUpdate({ clazz: REGULATION_DTO_CLAZZ_VALUE, mcdmRequired: false });
            case MCDMKind.YES:
                return this.regulationStateUpdate({ clazz: REGULATION_DTO_CLAZZ_VALUE, mcdmRequired: true });
            case MCDMKind.MCDM_ONLY:
                return this.regulationStateUpdate({ clazz: MCDM_ONLY_DTO_CLAZZ_VALUE, mcdmRequired: true });
        }
    };

    updateLocationDescription = (description: string) => {
        this.regulationStateUpdate({
            location: {
                clazz: this.state.regulation?.location?.clazz || TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
                id: this.state.regulation?.location?.id,
                description: description,
                flightLevels: this.state.regulation?.location?.flightLevels,
                refLocation: this.state.regulation?.location?.refLocation,
                tvSetIds: this.state.regulation?.location?.tvSetIds,
            },
        });
    };
    isApplied = () => {
        if (this.isProposal()) {
            const regulationProposal: RegulationProposalDto = this.state.regulation;
            return this.state.regulation && regulationProposal.approvalState !== McdmApprovalState.UNKNOWN;
        }
        return (
            this.state.regulation && MeasureUtils.getRegulationState(this.state.regulation) === RegulationState.APPLIED
        );
    };
    isCreateMode = () => {
        if (this.isProposal()) {
            return !this.state.regulation || Utils.isNotExists(this.state.regulation.dataId);
        }
        return !this.state.regulation || Utils.isNotExists(MeasureUtils.getRegulationState(this.state.regulation));
    };
    isUpdated = () => {
        return this.state.outdated;
    };
    calculateAutoPeriod = () => {
        this.setState({
            loadingAutoPeriod: true,
        });
        // Make a copy of the regulation without the end time
        const regulation = {
            ...this.state.regulation,
            applicability: { ...this.state.regulation.applicability, to: null },
        };

        ServiceFactory.sol49Service()
            .getMeasureCalculatedPeriod({
                regulation: this.adaptRegulationForB2BRequest(regulation),
                datasetReference: AppContext.getDatasetReference(),
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({
                        message: 'Error',
                        description: 'Failed to calculate measure period',
                        duration: 0,
                    });
                } else {
                    console.log('Measure Calculated Period ', TimeUtils.formatTimePeriod(reply.data.calculatedPeriod));
                    if (Utils.isExists(this.state.regulation?.applicability?.from)) {
                        this.updateEndTime(reply.data.calculatedPeriod.to);
                    } else {
                        this.updateStartTime(reply.data.calculatedPeriod.from);
                    }
                }
            })
            .catch(reason => {
                notification.error({ message: 'Error', description: reason.message, duration: 0 });
            })
            .finally(() => {
                this.setState({
                    loadingAutoPeriod: false,
                });
            });
    };
    handleSlotListClick = () => {
        const sidePanelContent = (
            <SlotListPanel measureId={this.state.regulation?.measureId} onClose={this.props.onClose} />
        );
        this.setState(
            { sidePanelVisible: true, sidePanelContent: sidePanelContent, sidePanelWidth: SlotListPanel.INITIAL_WIDTH },
            () => this.props.onWidthChange && this.props.onWidthChange('165em'),
        );
    };
    handleShowImpact = () => {
        this.setState({ loading: true });

        const solution: Solution = {
            measures: [this.adaptRegulationForB2BRequest(this.state.regulation)],
            name: 'NM PROPOSAL',
            type: SolutionType.ALTERNATIVE,
        };
        const tvAndPeriod = {
            timePeriod: this.state.regulation.applicability,
            tvId: MeasureUtils.getTvLocation(this.state.regulation).id,
            selected: true,
        };
        ServiceFactory.sol49Service()
            .getDetailedSolutions({
                solutions: [solution],
                capacityModel: CapacityModel.CCM,
                tvAndPeriods: [tvAndPeriod],
                monitoredPeriod: this.state.timePeriod,
                dataset: AppContext.getDataset(),
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.details, duration: 0 });
                } else if (reply.data.errors?.length) {
                    notification.error({
                        message: 'Find solutions returned errors - check console',
                        description: reply.data.errors.map((err, idx) => <span key={idx}>{err.details}</span>),
                        duration: 0,
                    });
                    console.log('Find solutions returned errors: ', reply.data.errors);
                } else {
                    const solutionImpact = (
                        <SolutionImpactPanel solution={reply.data.solutions[0]} onShareSolution={this.props.onClose} />
                    );
                    this.setState(
                        {
                            sidePanelVisible: true,
                            sidePanelContent: solutionImpact,
                            sidePanelWidth: SolutionImpactPanel.INITIAL_WIDTH,
                        },
                        () => this.props.onWidthChange && this.props.onWidthChange('150em'),
                    );
                }
            })
            .catch(err => {
                console.log('Errror fetching solution impact:', err);
                notification.error({
                    message: 'Error fetching solution impact',
                    description: err,
                });
            })
            .finally(() => {
                this.setState({
                    loading: false,
                });
            });
    };
    hideSidePanel = () => {
        this.setState({ sidePanelVisible: false, sidePanelContent: null }, () =>
            this.props.onWidthChange?.(RegulationEditorPanel.INITIAL_WIDTH),
        );
    };
    handleSelectedPeriodChange = (
        countType: CountType,
        selectedPeriod: TimePeriod,
        trafficWindow: TimePeriod,
        hourlyEntryCapacity?: Array<CapacityValue>,
        otmvs?: Array<Otmv>,
    ) => {
        if (countType === CountType.ENTRY && hourlyEntryCapacity) {
            const regInitialConstraints = MeasureUtils.hourlyEntryCapacityToRegInitialConstraints(
                hourlyEntryCapacity,
                selectedPeriod,
            );
            return this.regulationStateUpdate({
                applicability: selectedPeriod,
                initialConstraints: regInitialConstraints,
                supplementaryConstraints: null,
            });
        } else if (countType === CountType.OCCUPANCY) {
            const occupancyConstraints = MeasureUtils.otmvsToRegOccupancyConstraints(otmvs, selectedPeriod);
            return this.regulationStateUpdate({
                applicability: selectedPeriod,
                occupancyConstraints: occupancyConstraints,
            });
        } else {
            return this.regulationStateUpdate({
                applicability: selectedPeriod,
            });
        }
    };
    renderSidePanel = () => {
        if (!this.state.sidePanelVisible) return undefined;

        return [
            <SidePanelSeparator key="side-panel-separator" onHideSidePanel={this.hideSidePanel} />,
            <Flex key="side-panel-content" className="side-panel-content" size={this.state?.sidePanelWidth}>
                {this.state.sidePanelContent}
            </Flex>,
        ];
    };

    getEditorButtons = (): ReactNode[] => {
        const buttons = [];
        if (this.props.readonly) return buttons;

        const isValid = MeasureUtils.isValidForSimulation(this.state.regulation);
        const createEnabled = isValid && this.isCreateMode();
        const cancelEnabled = isValid && this.isApplied();
        const updateEnabled = isValid && this.isApplied() && this.isUpdated();

        if (this.isProposal()) {
            buttons.push(
                <Button
                    key="create-proposal"
                    onClick={() => this.createProposal(RegulationProposalAction.CREATION)}
                    disabled={!createEnabled}>
                    Create proposal
                </Button>,
            );
            // TODO UPDATE/ABANDON PROPOSAL
        } else {
            if (AuthorisationUtils.isFmpUser()) {
                buttons.push(
                    <Button
                        key="apply"
                        onClick={() => this.createProposal(RegulationProposalAction.UPDATE)}
                        disabled={!updateEnabled}>
                        Propose update
                    </Button>,
                );
                buttons.push(
                    <Button
                        key="cancel"
                        onClick={() => this.createProposal(RegulationProposalAction.CANCELLATION)}
                        disabled={!cancelEnabled}>
                        Propose cancel
                    </Button>,
                );
            } else if (AppContext.isNmvpBaseline()) {
                // NM user is NMVP
                buttons.push(
                    <Button key="create" onClick={this.createRegulation} disabled={!createEnabled}>
                        Create
                    </Button>,
                );
                buttons.push(
                    <Button key="update" onClick={this.updateRegulation} disabled={!updateEnabled}>
                        Update
                    </Button>,
                );
                buttons.push(
                    <Button key="cancel" onClick={this.cancelRegulation} disabled={!cancelEnabled}>
                        Cancel
                    </Button>,
                );
            } else {
                // NM user in PREOPS/OPS
                buttons.push(
                    <Button
                        key="propose-creation"
                        onClick={() => this.createProposal(RegulationProposalAction.CREATION)}
                        disabled={AppContext.isOpsBaseline() || !createEnabled}>
                        Propose creation
                    </Button>,
                );
                buttons.push(
                    <Button
                        key="propose-update"
                        onClick={() => this.createProposal(RegulationProposalAction.UPDATE)}
                        disabled={AppContext.isOpsBaseline() || !updateEnabled}>
                        Propose update
                    </Button>,
                );
                buttons.push(
                    <Button
                        key="propose-cancel"
                        onClick={() => this.createProposal(RegulationProposalAction.CANCELLATION)}
                        disabled={AppContext.isOpsBaseline() || !cancelEnabled}>
                        Propose cancel
                    </Button>,
                );
            }
        }

        buttons.push(
            <Button key="show-impact" onClick={this.handleShowImpact} disabled={!this.isUpdated() || !isValid}>
                Show Impact
            </Button>,
        );

        return buttons;
    };

    render() {
        const regulationId =
            this.state.regulation?.measureId?.id || (this.props.idType === 'reg' && this.props.id) || undefined;
        const regPeriods = MeasureUtils.getRegulationPeriods(this.state.regulation);
        const regulationPeriods = regPeriods && [regPeriods];
        return (
            <Flex>
                <Flex className="regulation-editor" direction="vertical">
                    <Spin spinning={this.state.loading}>
                        <Flex className="modal-title header">
                            <ConditionalRender condition={Utils.isExists(this.state.lastUpdate)}>
                                <Flex className="update-time" size="15em">
                                    last update{' '}
                                    {TimeUtils.formatDateDDMMYYYY(this.state.lastUpdate, '-') +
                                        ' ' +
                                        TimeUtils.formatHHMM(this.state.lastUpdate)}
                                </Flex>
                            </ConditionalRender>
                            <Flex direction="vertical">
                                <Flex className="regulation-id">
                                    REGULATION {this.isProposal() ? 'PROPOSAL' : ''}{' '}
                                    {regulationId ? '- ' + regulationId : null}
                                </Flex>
                                <Flex className="regulation-state">
                                    {this.state.regulation && MeasureUtils.getRegulationState(this.state.regulation)}
                                </Flex>
                            </Flex>
                            <Flex size="15em" />
                        </Flex>
                        <TvDetails
                            currentTime={this.props.currentTime}
                            timePeriod={this.state.timePeriod}
                            tvId={this.state.regulation?.location?.id}
                            tvSet={
                                this.state.regulation?.location?.tvSetIds &&
                                this.state.regulation?.location?.tvSetIds[0]
                            }
                            regulationPeriods={!this.isProposal() ? regulationPeriods : undefined}
                            proposalRegulationPeriods={this.isProposal() ? regulationPeriods : undefined}
                            onUpdate={() => this.setState({ lastUpdate: AppContext.getCurrentTime() })}
                            onSelectedPeriodChange={this.handleSelectedPeriodChange}
                            selectedPeriodType={this.state.countType}
                            onCountTypeChange={countType =>
                                this.setState({ countType: countType }, () =>
                                    this.regulationStateUpdate({ calculationType: countType }),
                                )
                            }
                            onOtmvDurationChange={duration =>
                                this.regulationStateUpdate({ occupancyDurationInMs: duration * TimeUtils.MIN_TO_MS })
                            }
                        />{' '}
                        {/* TODO: Deal with more than 1 TV set */}
                        <Divider type="horizontal" />
                        <div className="regulation-info">
                            <Row gutter={[8, 8]}>
                                <Col span={5}>
                                    Identifier
                                    <Input
                                        value={this.state.regulation?.measureId?.id}
                                        onChange={value => this.updateIdentifier(value.currentTarget.value)}
                                        disabled={!this.isCreateMode()}
                                    />
                                </Col>
                                <Col span={4}>
                                    Traffic Volume
                                    <Input
                                        value={this.state.regulation?.location?.id}
                                        onChange={value => this.updateTvId(value.currentTarget.value)}
                                        disabled={
                                            this.state.regulation &&
                                            Utils.isExists(MeasureUtils.getRegulationState(this.state.regulation))
                                        }
                                    />
                                </Col>
                                <Col span={3}>
                                    FMP
                                    <Input value={this.state.regulation?.delayTvSetId} disabled={true} />
                                </Col>
                                <Col span={6}>
                                    Regulation type
                                    <Select
                                        disabled={this.props.readonly}
                                        value={getDisplayMeasureType(this.state.regulation)}
                                        onChange={value => this.updateMeasureType(value)}>
                                        <Select.Option value={DisplayMeasureType.NETWORK_MEASURE}>
                                            Network Measure
                                        </Select.Option>
                                        <Select.Option value={DisplayMeasureType.FMP_MEASURE}>
                                            FMP Measure
                                        </Select.Option>
                                        <Select.Option value={DisplayMeasureType.NETWORK_CHERRY_PICK_MEASURE}>
                                            Network Cherry Pick Measure
                                        </Select.Option>
                                        <Select.Option value={DisplayMeasureType.FMP_CHERRY_PICK_MEASURE}>
                                            FMP Cherry Pick Measure
                                        </Select.Option>
                                    </Select>
                                </Col>
                                <Col span={5}>
                                    Subtype
                                    <Select
                                        disabled={this.props.readonly}
                                        value={this.state.regulation?.subType}
                                        onChange={value => this.regulationStateUpdate({ subType: value })}>
                                        <Select.Option value={MeasureSubType.GROUND_DELAY}>Ground Delay</Select.Option>
                                        <Select.Option value={MeasureSubType.TAKE_OFF_NOT_BEFORE}>
                                            Take Off Not Before
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.TAKE_OFF_NOT_AFTER}>
                                            Take Off Not After
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.MINIMUM_DEPARTURE_INTERVAL}>
                                            Min Departure Interval
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.GROUND_LEVEL_CAP}>
                                            GroundLevel Cap
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.GROUND_HORIZONTAL_REROUTING}>
                                            Ground Horizontal Rerouting
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.AIRBORNE_LEVEL_CAP}>
                                            ATC Level Cap
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.AIRBORNE_HORIZONTAL_REROUTING}>
                                            ATC Horizontal Rerouting
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.TERMINAL_PROCEDURE_CHANGE}>
                                            Change of Terminal Procedure
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.MILES_MINUTES_IN_TRAIL}>
                                            Miles In Trail
                                        </Select.Option>
                                        <Select.Option value={MeasureSubType.OTHER_KIND_OF_STAM_MEASURE}>
                                            Other
                                        </Select.Option>
                                    </Select>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={6}>
                                    Reason
                                    <Select
                                        disabled={this.props.readonly}
                                        value={this.state.regulation?.reason}
                                        onChange={value => this.regulationStateUpdate({ reason: value })}
                                        listHeight={320}>
                                        <Select.Option value={RegulationReasonType.ACCIDENT_INCIDENT}>
                                            Accident/Incident
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.AERODROME_CAPACITY}>
                                            Aerodrome Capacity
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ATC_INDUSTRIAL_ACTION}>
                                            ATC Ind Action
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.AIRSPACE_MANAGEMENT}>
                                            Airspace Management
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.OTHERS}>Other</Select.Option>
                                        <Select.Option value={RegulationReasonType.SPECIAL_EVENT}>
                                            Special Event
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ATC_ROUTINGS}>
                                            ATC Routings
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ATC_STAFFING}>
                                            ATC Staffing
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ATC_EQUIPMENT}>
                                            ATC Equipment
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ENVIRONMENTAL_ISSUES}>
                                            Environmental Issues
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.WEATHER}>Weather</Select.Option>
                                        <Select.Option value={RegulationReasonType.AERODROME_SERVICES}>
                                            Aerodrome Services
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.NON_ATC_INDUSTRIAL_ACTION}>
                                            Ind Action non-ATC
                                        </Select.Option>
                                        <Select.Option value={RegulationReasonType.ATC_CAPACITY}>
                                            ATC Capacity
                                        </Select.Option>
                                    </Select>
                                </Col>
                                <Col span={3}>
                                    Min Level
                                    <Input
                                        disabled={true}
                                        value={FlightLevelUtils.getLevelValue(
                                            this.state.regulation?.location?.flightLevels?.min,
                                        )}
                                    />
                                </Col>
                                <Col span={3}>
                                    Max Level
                                    <Input
                                        disabled={true}
                                        value={FlightLevelUtils.getLevelValue(
                                            this.state.regulation?.location?.flightLevels?.max,
                                        )}
                                    />
                                </Col>
                                <ConditionalRender condition={MeasureUtils.isEntryRegulation(this.state.regulation)}>
                                    <Col span={4} className={this.state.windowWidthBlinking ? 'blink' : ''}>
                                        Window width
                                        <InputDuration
                                            disabled={this.props.readonly}
                                            value={this.state.regulation?.noDelayWindowInMs}
                                            onChange={value => this.regulationStateUpdate({ noDelayWindowInMs: value })}
                                        />
                                    </Col>
                                </ConditionalRender>
                                <Col span={4}>
                                    Delay threshold
                                    <InputDuration
                                        disabled={this.props.readonly}
                                        value={this.state.regulation?.delayConfirmationThresholdInMs}
                                        onChange={value =>
                                            this.regulationStateUpdate({ delayConfirmationThresholdInMs: value })
                                        }
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={3}>
                                    Start time
                                    <InputTime
                                        disabled={this.props.readonly}
                                        value={this.state.regulation?.applicability?.from}
                                        onChange={value => this.updateStartTime(value)}
                                    />
                                </Col>
                                <Col span={3}>
                                    End Time
                                    <InputTime
                                        disabled={this.props.readonly}
                                        value={this.state.regulation?.applicability?.to}
                                        onChange={value => this.updateEndTime(value)}
                                    />
                                    <span className="spinner-end-time">
                                        {this.state.loadingAutoPeriod && <LoadingOutlined />}
                                        {!this.state.loadingAutoPeriod && (
                                            <EnhancedIcon
                                                disabled={
                                                    this.props.readonly ||
                                                    !MeasureUtils.isValidForSimulation(this.state.regulation)
                                                }
                                                onClick={() => this.calculateAutoPeriod()}>
                                                <RedoOutlined />
                                            </EnhancedIcon>
                                        )}
                                    </span>
                                </Col>
                                <Col span={3}>
                                    Duration
                                    <InputDuration
                                        disabled={this.props.readonly}
                                        value={this.getDuration()}
                                        onChange={value => this.updateDuration(value)}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <ConditionalRender condition={this.state.countType === CountType.ENTRY}>
                                    <Col span={14}>
                                        <InitialConstraintsEditor
                                            readonly={this.props.readonly}
                                            regulation={this.state.regulation}
                                            onInitialConstraintsUpdate={constraints =>
                                                this.regulationStateUpdate(constraints)
                                            }
                                        />
                                    </Col>
                                </ConditionalRender>
                                <ConditionalRender condition={this.state.countType === CountType.OCCUPANCY}>
                                    <Col span={14}>
                                        <OccupancyConstraintsEditor
                                            readonly={this.props.readonly}
                                            regulation={this.state.regulation}
                                            onOccupancyConstraintsUpdate={constraints =>
                                                this.regulationStateUpdate(constraints)
                                            }
                                        />
                                    </Col>
                                </ConditionalRender>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={5}>
                                    <Checkbox
                                        disabled={this.props.readonly}
                                        checked={this.state.regulation?.updateCapacityRequired}
                                        onChange={e =>
                                            this.regulationStateUpdate({ updateCapacityRequired: e.target.checked })
                                        }>
                                        Update capacity
                                    </Checkbox>
                                </Col>
                                <Col span={6}>
                                    <Checkbox
                                        disabled={this.props.readonly}
                                        checked={this.state.regulation?.updateTvActivationRequired}
                                        onChange={e =>
                                            this.regulationStateUpdate({ updateTvActivationRequired: e.target.checked })
                                        }>
                                        Update TV activation
                                    </Checkbox>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={7}>
                                    MCDM Kind
                                    <Select
                                        disabled={this.props.readonly}
                                        value={getMcdmKind(this.state.regulation)}
                                        onChange={value => this.updateMcdmKind(value)}>
                                        <Select.Option value={MCDMKind.NO}>No</Select.Option>
                                        <Select.Option value={MCDMKind.YES}>Yes</Select.Option>
                                        <Select.Option value={MCDMKind.MCDM_ONLY}>MCDM Only</Select.Option>
                                    </Select>
                                </Col>
                                <Col span={8}>
                                    <br />
                                    <Button disabled={this.props.readonly}>View coordination</Button>
                                </Col>
                                <Col span={6}>
                                    <br />
                                    <Space>
                                        <Button disabled={this.props.readonly}>Link</Button>
                                        <Checkbox
                                            disabled={this.props.readonly}
                                            checked={this.state.regulation?.autoLink}
                                            onChange={e => this.regulationStateUpdate({ autoLink: e.target.checked })}>
                                            Auto Link
                                        </Checkbox>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={15}>
                                    ANM TV Description
                                    <TextArea
                                        value={this.state.regulation?.location?.description}
                                        onChange={value => this.updateLocationDescription(value.currentTarget.value)}
                                        rows={4}
                                        disabled={this.props.readonly}
                                    />
                                </Col>
                                <Col span={9}>
                                    ANM remark
                                    <TextArea
                                        value={this.state.regulation?.remark}
                                        onChange={value =>
                                            this.regulationStateUpdate({ remark: value.currentTarget.value })
                                        }
                                        rows={4}
                                        disabled={this.props.readonly}
                                    />
                                </Col>
                            </Row>
                            <Row gutter={[8, 8]}>
                                <Col span={24}>
                                    <ConditionalRender
                                        condition={
                                            AppContext.isNmvpBaseline() && !this.isProposal() && !this.isCreateMode()
                                        }>
                                        <SlotListSynthesisForMeasure
                                            measureId={this.state.regulation?.measureId}
                                            slotListButtonVisible={
                                                !this.isProposal() && !AuthorisationUtils.isFmpMapUser()
                                            }
                                            onSlotListClick={this.handleSlotListClick}
                                        />
                                    </ConditionalRender>
                                </Col>
                            </Row>
                        </div>
                        <Flex className="buttons">
                            <RegulationEditorButtonPanel onClose={this.props.onClose}>
                                {this.getEditorButtons()}
                            </RegulationEditorButtonPanel>
                        </Flex>
                    </Spin>
                </Flex>
                {this.renderSidePanel()}
            </Flex>
        );
    }
}

interface RegulationEditorButtonPanelProps extends ModalPanelProps {
    children: ReactNode[];
}

class RegulationEditorButtonPanel extends PureComponent<RegulationEditorButtonPanelProps> {
    render() {
        return (
            <Space>
                <Button key="close" onClick={this.props.onClose}>
                    Close
                </Button>
                {this.props.children}
            </Space>
        );
    }
}
